"use client";

import { FormEvent, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RESPONSE_TIME, SUPPORT_EMAIL } from "@/config/contact";
import { GHL_CONTACT_FORM_ID, GHL_FIELD_MESSAGE } from "@/config/ghl";
import { trackGhlFormSubmission } from "@/lib/ghl-tracking";

// Public contact form. On submit it does TWO independent things, in this order
// of importance:
//
//   1. Posts to /api/support-message (Slack). This is the REAL delivery path —
//      a human reads it and replies. Its failure IS surfaced to the user,
//      because a message nobody received must never look sent.
//   2. Mirrors the lead into GoHighLevel. Fire-and-forget: ad blockers block
//      that endpoint routinely, and losing a CRM row must not cost the visitor
//      their message.
//
// FIELD NAMING IS LOAD-BEARING. GoHighLevel's external-tracking script attaches
// its OWN capture-phase "submit" listener to this form, so it posts an event on
// every submit no matter what we do, building formData from each input's `name`
// attribute. The names below are therefore GHL's own contact keys (first_name /
// last_name / email / phone are built-ins; the message is the custom field
// verified to exist on the location). That makes the script's automatic event
// and our explicit one carry IDENTICAL, correctly-mapped data, which GHL merges
// onto a single contact by email — instead of the mismatched "Unidentified
// Form" duplicate that raw names like "name" and "message" produced. Rename an
// input here and the CRM record silently corrupts; change it only together with
// config/ghl.ts.
export function ContactForm() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);
    if (!message.trim()) {
      setError("Please enter a message.");
      return;
    }
    if (!email.trim()) {
      setError("Please enter your email so we can reply.");
      return;
    }
    setIsSubmitting(true);

    // CRM capture first and non-blocking: if the visitor closes the tab while
    // the Slack request is still in flight, the lead is already recorded.
    trackGhlFormSubmission({
      formId: GHL_CONTACT_FORM_ID,
      formData: {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        [GHL_FIELD_MESSAGE]: message.trim()
      },
      formLabels: {
        first_name: "First Name",
        last_name: "Last Name",
        email: "Email Address",
        phone: "Phone Number",
        [GHL_FIELD_MESSAGE]: "How can we help?"
      }
    });

    // The support route accepts message/email/context only, so the name and
    // phone are prepended to the body rather than being dropped.
    const fullName = `${firstName} ${lastName}`.trim();
    const header = [fullName ? `Name: ${fullName}` : null, phone.trim() ? `Phone: ${phone.trim()}` : null].filter(Boolean);
    const slackMessage = header.length ? header.join("\n") + "\n\n" + message : message;
    try {
      const response = await fetch("/api/support-message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          message: slackMessage,
          email: email.trim(),
          context: "Contact page"
        })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error((data as {
          error?: string;
        }).error || "Failed to send message.");
      }
      setSuccess(true);
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <form onSubmit={handleSubmit} className="space-y-5"
  // Read by the GHL script to name its own event; without it every
  // submission is filed under "Unidentified Form".
  data-formid={GHL_CONTACT_FORM_ID}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="contact-first-name">
            First name <span className="text-red-500">*</span>
          </Label>
          <Input id="contact-first-name" name="first_name" required autoComplete="given-name" placeholder="Jane" className="h-11" value={firstName} onChange={e => setFirstName(e.target.value)} disabled={isSubmitting} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact-last-name">Last name</Label>
          <Input id="contact-last-name" name="last_name" autoComplete="family-name" placeholder="Doe" className="h-11" value={lastName} onChange={e_0 => setLastName(e_0.target.value)} disabled={isSubmitting} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="contact-email">
            Work email <span className="text-red-500">*</span>
          </Label>
          <Input id="contact-email" name="email" type="email" required autoComplete="email" placeholder="you@company.com" className="h-11" value={email} onChange={e_1 => setEmail(e_1.target.value)} disabled={isSubmitting} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact-phone">Phone number</Label>
          <Input id="contact-phone" name="phone" type="tel" autoComplete="tel" placeholder="+1 (555) 000-0000" className="h-11" value={phone} onChange={e_2 => setPhone(e_2.target.value)} disabled={isSubmitting} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="contact-message">
          How can we help? <span className="text-red-500">*</span>
        </Label>
        <textarea id="contact-message" name={GHL_FIELD_MESSAGE} required placeholder="Tell us what you're trying to build — coverage questions, a plan for your team, billing, or anything else." className="min-h-[150px] w-full rounded-md border border-line bg-card px-3 py-2.5 text-sm text-ink-soft shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" value={message} onChange={e_3 => setMessage(e_3.target.value)} disabled={isSubmitting} />
      </div>

      {error && <p className="text-sm text-red-600" role="alert">
          {error}
        </p>}
      {success && <p className="text-sm text-emerald-600" role="status">
          Message sent — we&apos;ll reply {RESPONSE_TIME}.
        </p>}

      <div className="flex flex-wrap items-center gap-4">
        <Button type="submit" disabled={isSubmitting} className="h-[48px] px-7 rounded-[6px] text-[16px] font-medium">
          {isSubmitting ? "Sending..." : "Send message"}
        </Button>
        <p className="text-sm text-muted-ink">
          Prefer email?{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
            {SUPPORT_EMAIL}
          </a>
        </p>
      </div>
    </form>;
}